import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePortfolio } from '../../context/PortfolioContext';
import { getCalendarEvents } from '../../services/api';
import { Calendar, ChevronLeft, ChevronRight, TrendingUp, DollarSign } from 'lucide-react';
// @ts-ignore
import Spinner from '../../components/Spinner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CalendarEvent {
  ticker: string;
  type: 'earnings' | 'dividend';
  date: string; // YYYY-MM-DD
  details: {
    revenue_estimate?: number;
    eps_estimate?: number;
    dividend_rate?: number;
    dividend_yield?: number;
  };
}

interface CalendarResponse {
  events: CalendarEvent[];
  tickers_count: number;
}

type SourceType = 'watchlist' | 'portfolio' | 'custom';
type ViewMode = 'timeline' | 'calendar';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatShortDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getWeekKey(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDay();
  const sun = new Date(d);
  sun.setDate(d.getDate() - day);
  const sat = new Date(sun);
  sat.setDate(sun.getDate() + 6);
  return `${sun.toISOString().slice(0, 10)}|${sat.toISOString().slice(0, 10)}`;
}

function getWeekLabel(weekKey: string): string {
  const [start, end] = weekKey.split('|');
  return `${formatShortDate(start)} - ${formatShortDate(end)}`;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

// ---------------------------------------------------------------------------
// Timeline View
// ---------------------------------------------------------------------------

const TimelineView: React.FC<{ events: CalendarEvent[] }> = ({ events }) => {
  const sorted = [...events].sort((a, b) => a.date.localeCompare(b.date));

  const weekGroups: { key: string; label: string; events: CalendarEvent[] }[] = [];
  const weekMap = new Map<string, CalendarEvent[]>();

  for (const evt of sorted) {
    const wk = getWeekKey(evt.date);
    if (!weekMap.has(wk)) weekMap.set(wk, []);
    weekMap.get(wk)!.push(evt);
  }

  Array.from(weekMap.entries()).forEach(([key, evts]) => {
    weekGroups.push({ key, label: getWeekLabel(key), events: evts });
  });

  if (weekGroups.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 text-center">
        <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 text-sm">No upcoming events found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {weekGroups.map((group) => (
        <div key={group.key}>
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            {group.label}
          </h3>
          <div className="space-y-3">
            {group.events.map((evt, idx) => (
              <div
                key={`${evt.ticker}-${evt.date}-${idx}`}
                className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-base font-bold text-slate-900">{evt.ticker}</span>
                    {evt.type === 'earnings' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        <TrendingUp className="w-3 h-3" />
                        Earnings
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                        <DollarSign className="w-3 h-3" />
                        Dividend
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-slate-500">{formatDate(evt.date)}</span>
                </div>

                <div className="mt-3 flex flex-wrap gap-4 text-sm">
                  {evt.type === 'earnings' && (
                    <>
                      {evt.details.eps_estimate != null && (
                        <div>
                          <span className="text-xs text-slate-400">EPS Est.</span>
                          <p className="font-semibold text-slate-700">${evt.details.eps_estimate.toFixed(2)}</p>
                        </div>
                      )}
                      {evt.details.revenue_estimate != null && (
                        <div>
                          <span className="text-xs text-slate-400">Revenue Est.</span>
                          <p className="font-semibold text-slate-700">
                            ${(evt.details.revenue_estimate / 1e9).toFixed(2)}B
                          </p>
                        </div>
                      )}
                    </>
                  )}
                  {evt.type === 'dividend' && (
                    <>
                      {evt.details.dividend_rate != null && (
                        <div>
                          <span className="text-xs text-slate-400">Dividend Rate</span>
                          <p className="font-semibold text-slate-700">${evt.details.dividend_rate.toFixed(2)}</p>
                        </div>
                      )}
                      {evt.details.dividend_yield != null && (
                        <div>
                          <span className="text-xs text-slate-400">Dividend Yield</span>
                          <p className="font-semibold text-slate-700">{(evt.details.dividend_yield * 100).toFixed(2)}%</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Calendar Grid View
// ---------------------------------------------------------------------------

const CalendarGridView: React.FC<{ events: CalendarEvent[] }> = ({ events }) => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const monthLabel = new Date(year, month, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const prevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
    setSelectedDay(null);
  };

  const nextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
    setSelectedDay(null);
  };

  // Build a map of dateStr -> events for the current month
  const eventsByDate = new Map<string, CalendarEvent[]>();
  for (const evt of events) {
    const d = new Date(evt.date + 'T00:00:00');
    if (d.getFullYear() === year && d.getMonth() === month) {
      const key = evt.date;
      if (!eventsByDate.has(key)) eventsByDate.set(key, []);
      eventsByDate.get(key)!.push(evt);
    }
  }

  // Cells: leading blanks + days
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectedEvents = selectedDay ? eventsByDate.get(selectedDay) || [] : [];

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={prevMonth}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h3 className="text-lg font-semibold text-slate-800">{monthLabel}</h3>
          <button
            onClick={nextMonth}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 mb-1">
          {dayHeaders.map((dh) => (
            <div key={dh} className="text-center text-xs font-medium text-slate-400 py-2">
              {dh}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {cells.map((day, idx) => {
            if (day === null) {
              return <div key={`blank-${idx}`} className="min-h-[80px] border border-slate-100 p-1" />;
            }

            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayEvents = eventsByDate.get(dateStr) || [];
            const isTodayCell =
              today.getFullYear() === year &&
              today.getMonth() === month &&
              today.getDate() === day;
            const isSelected = selectedDay === dateStr;

            return (
              <div
                key={dateStr}
                onClick={() => setSelectedDay(isSelected ? null : dateStr)}
                className={`min-h-[80px] border border-slate-100 p-1 cursor-pointer transition-colors hover:bg-slate-50 ${
                  isTodayCell ? 'ring-2 ring-blue-500' : ''
                } ${isSelected ? 'bg-blue-50' : ''}`}
              >
                <span
                  className={`text-xs font-medium ${
                    isTodayCell ? 'text-blue-600 font-bold' : 'text-slate-600'
                  }`}
                >
                  {day}
                </span>
                {dayEvents.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {dayEvents.map((evt, i) => (
                      <span
                        key={`${evt.ticker}-${i}`}
                        className={`inline-block w-2 h-2 rounded-full ${
                          evt.type === 'earnings' ? 'bg-blue-500' : 'bg-emerald-500'
                        }`}
                        title={`${evt.ticker} - ${evt.type}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected day detail */}
      {selectedDay && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h4 className="text-sm font-semibold text-slate-700 mb-3">
            Events on {formatDate(selectedDay)}
          </h4>
          {selectedEvents.length === 0 ? (
            <p className="text-sm text-slate-400">No events on this day.</p>
          ) : (
            <div className="space-y-3">
              {selectedEvents.map((evt, idx) => (
                <div
                  key={`${evt.ticker}-${idx}`}
                  className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-base font-bold text-slate-900">{evt.ticker}</span>
                    {evt.type === 'earnings' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        <TrendingUp className="w-3 h-3" />
                        Earnings
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                        <DollarSign className="w-3 h-3" />
                        Dividend
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-4 text-sm">
                    {evt.type === 'earnings' && (
                      <>
                        {evt.details.eps_estimate != null && (
                          <div>
                            <span className="text-xs text-slate-400">EPS Est.</span>
                            <p className="font-semibold text-slate-700">
                              ${evt.details.eps_estimate.toFixed(2)}
                            </p>
                          </div>
                        )}
                        {evt.details.revenue_estimate != null && (
                          <div>
                            <span className="text-xs text-slate-400">Revenue Est.</span>
                            <p className="font-semibold text-slate-700">
                              ${(evt.details.revenue_estimate / 1e9).toFixed(2)}B
                            </p>
                          </div>
                        )}
                      </>
                    )}
                    {evt.type === 'dividend' && (
                      <>
                        {evt.details.dividend_rate != null && (
                          <div>
                            <span className="text-xs text-slate-400">Dividend Rate</span>
                            <p className="font-semibold text-slate-700">
                              ${evt.details.dividend_rate.toFixed(2)}
                            </p>
                          </div>
                        )}
                        {evt.details.dividend_yield != null && (
                          <div>
                            <span className="text-xs text-slate-400">Dividend Yield</span>
                            <p className="font-semibold text-slate-700">
                              {(evt.details.dividend_yield * 100).toFixed(2)}%
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main View
// ---------------------------------------------------------------------------

const EarningsCalendarView: React.FC = () => {
  const { currentUser } = useAuth();
  const { activePortfolioId } = usePortfolio();

  const [source, setSource] = useState<SourceType>('watchlist');
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const [customTickers, setCustomTickers] = useState('');
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [tickersCount, setTickersCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(
    async (src: SourceType, customInput?: string) => {
      if (!currentUser) return;
      setLoading(true);
      setError(null);

      try {
        const token = await currentUser.getIdToken();

        let portfolioId: string | null = null;

        if (src === 'portfolio') {
          portfolioId = activePortfolioId;
        }

        // For custom, we pass the tickers as the source parameter
        const sourceParam = src === 'custom' && customInput ? customInput.trim() : src;

        const data: CalendarResponse = await getCalendarEvents(
          token,
          sourceParam,
          portfolioId
        );

        setEvents(data.events || []);
        setTickersCount(data.tickers_count || 0);
      } catch (err: any) {
        setError(err.message || 'Failed to load calendar events');
        setEvents([]);
        setTickersCount(0);
      } finally {
        setLoading(false);
      }
    },
    [currentUser, activePortfolioId]
  );

  // Auto-fetch on source change (watchlist or portfolio)
  useEffect(() => {
    if (source !== 'custom') {
      fetchEvents(source);
    }
  }, [source, fetchEvents]);

  const handleSourceChange = (newSource: SourceType) => {
    setSource(newSource);
    if (newSource === 'custom') {
      // Don't auto-fetch for custom; wait for Load button
      setEvents([]);
      setTickersCount(0);
    }
  };

  const handleLoadCustom = () => {
    if (customTickers.trim()) {
      fetchEvents('custom', customTickers);
    }
  };

  const activeBtn = 'bg-blue-600 text-white';
  const inactiveBtn = 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200';

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Earnings & Events Calendar</h1>
            <p className="text-sm text-slate-500">
              {tickersCount > 0
                ? `${tickersCount} tickers -- ${events.length} events`
                : 'Track earnings and dividend events for your holdings'}
            </p>
          </div>
        </div>

        {/* Controls bar */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Source selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-600 mr-2">Source:</span>
              <div className="flex rounded-lg overflow-hidden">
                <button
                  onClick={() => handleSourceChange('watchlist')}
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-l-lg ${
                    source === 'watchlist' ? activeBtn : inactiveBtn
                  }`}
                >
                  Watchlist
                </button>
                <button
                  onClick={() => handleSourceChange('portfolio')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    source === 'portfolio' ? activeBtn : inactiveBtn
                  }`}
                >
                  Portfolio
                </button>
                <button
                  onClick={() => handleSourceChange('custom')}
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-r-lg ${
                    source === 'custom' ? activeBtn : inactiveBtn
                  }`}
                >
                  Custom
                </button>
              </div>
            </div>

            {/* View toggle */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-600 mr-2">View:</span>
              <div className="flex rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('timeline')}
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-l-lg ${
                    viewMode === 'timeline' ? activeBtn : inactiveBtn
                  }`}
                >
                  Timeline
                </button>
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-r-lg ${
                    viewMode === 'calendar' ? activeBtn : inactiveBtn
                  }`}
                >
                  Calendar
                </button>
              </div>
            </div>
          </div>

          {/* Custom tickers input */}
          {source === 'custom' && (
            <div className="mt-4 flex items-center gap-3">
              <input
                type="text"
                value={customTickers}
                onChange={(e) => setCustomTickers(e.target.value)}
                placeholder="Enter tickers separated by commas (e.g., AAPL, MSFT, GOOGL)"
                className="flex-1 px-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-slate-400"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleLoadCustom();
                }}
              />
              <button
                onClick={handleLoadCustom}
                disabled={!customTickers.trim()}
                className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors ${
                  customTickers.trim()
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
              >
                Load
              </button>
            </div>
          )}
        </div>

        {/* Content area */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner />
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl border border-red-200 shadow-sm p-6 text-center">
            <p className="text-red-600 text-sm font-medium">{error}</p>
            <button
              onClick={() => fetchEvents(source, source === 'custom' ? customTickers : undefined)}
              className="mt-3 px-4 py-2 text-sm font-medium bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : events.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 text-center">
            <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No upcoming events found</p>
            <p className="text-slate-400 text-xs mt-1">
              {source === 'custom'
                ? 'Enter ticker symbols above and click Load'
                : 'Try switching to a different source'}
            </p>
          </div>
        ) : viewMode === 'timeline' ? (
          <TimelineView events={events} />
        ) : (
          <CalendarGridView events={events} />
        )}
      </div>
    </div>
  );
};

export default EarningsCalendarView;
